import React, { forwardRef, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Snackbar,
  Alert,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Divider,
  Link as MuiLink,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  useTheme
} from "@mui/material";
import { Link } from "react-router-dom";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditIcon from "@mui/icons-material/Edit";
import PreviewIcon from "@mui/icons-material/Preview";
import CodeIcon from "@mui/icons-material/Code";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useDispatch, useSelector } from "react-redux";
import {
  getCurrentUser,
  updateAdminCoachAgreement
} from "../../features/auth/authSlice";
import { selectCurrentSuperAdmin } from "../../features/auth/authSelectors";

const ManualCopyPaste = ({ currentAdmin }) => {
  const theme = useTheme();
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [activeStep, setActiveStep] = useState(0);
  const defaultValue = useMemo(
    () => currentAdmin?.agreementContent,
    [currentAdmin]
  );
  const [value, setValue] = useState(defaultValue);
  // Steps for the editor workflow
  const steps = [
    { label: "Copy HTML", description: "Copy the current HTML content" },
    { label: "Edit", description: "Edit in external HTML editor" },
    { label: "Update", description: "Paste and update the content" }
  ];

  const handleCopy = () => {
    navigator.clipboard.writeText(defaultValue || value);
    setSnackbarMessage("Content copied to clipboard!");
    setOpenSnackbar(true);
    setActiveStep(1);
  };
  const dispatch = useDispatch();
  const handleUpdate = () => {
    setSnackbarMessage("Content updated successfully!");
    setOpenSnackbar(true);
    setActiveStep(0);
    dispatch(updateAdminCoachAgreement({ agreementContent: value }));
    setTimeout(() => dispatch(getCurrentUser()), 1500);
  };

  return (
    <Card
      elevation={3}
      sx={{
        overflow: "visible",
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        mb: 4
      }}
    >
      <Box
        sx={{
          p: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
          background: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          borderRadius: "8px 8px 0 0",
          display: "flex",
          alignItems: "center"
        }}
      >
        <EditIcon sx={{ mr: 1 }} />
        <Typography variant="h6" fontWeight="medium">
          Content Editor
        </Typography>
      </Box>

      <CardContent sx={{ p: 3 }}>
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
          {steps.map((step, index) => (
            <Step key={step.label}>
              <StepLabel>
                <Typography variant="body2" fontWeight="medium">
                  {step.label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {step.description}
                </Typography>
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            backgroundColor: theme.palette.background.default,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <CodeIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="subtitle1" fontWeight="medium">
              Step 1: Copy HTML Content
            </Typography>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            First, copy your current HTML content to be edited. Use the button
            below:
          </Typography>

          <Button
            variant="contained"
            color="primary"
            startIcon={<ContentCopyIcon />}
            onClick={handleCopy}
            sx={{ mb: 2 }}
          >
            Copy HTML
          </Button>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            backgroundColor: theme.palette.background.default,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <PreviewIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="subtitle1" fontWeight="medium">
              Step 2: Edit in External HTML Editor
            </Typography>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Make your HTML changes in an external editor, then test the output
            by running it:
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <MuiLink
              component={Link}
              to="https://www.w3schools.com/html/tryit.asp?filename=tryhtml_default"
              target="_blank"
              sx={{
                display: "flex",
                alignItems: "center",
                textDecoration: "none",
                color: theme.palette.primary.main
              }}
            >
              <Button
                variant="outlined"
                color="primary"
                endIcon={<OpenInNewIcon />}
              >
                Open HTML Editor
              </Button>
            </MuiLink>
          </Box>

          <Box
            sx={{
              mb: 1,
              pl: 1,
              borderLeft: `3px solid ${theme.palette.info.main}`
            }}
          >
            <Typography variant="body2" color="text.secondary">
              <strong>Tip:</strong> After making changes, run the code to
              preview the result. If it looks correct, select all the HTML code
              and copy it.
            </Typography>
          </Box>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: 3,
            backgroundColor: theme.palette.background.default,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <EditIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="subtitle1" fontWeight="medium">
              Step 3: Paste Updated HTML
            </Typography>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Paste your modified HTML below and click Update:
          </Typography>

          <TextField
            fullWidth
            multiline
            maxRows={7}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Paste your modified HTML here..."
            variant="outlined"
            sx={{ mb: 2, backgroundColor: theme.palette.background.paper }}
          />

          <Button
            variant="contained"
            color="success"
            startIcon={<CheckCircleIcon />}
            onClick={handleUpdate}
            sx={{ mt: 1 }}
          >
            Update Content
          </Button>
        </Paper>
      </CardContent>

      <Snackbar
        open={openSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity="success"
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Card>
  );
};

export default ManualCopyPaste;
